import React from "react";

/**
 * Shared appearance primitives for BO-configured menu items, used by the left
 * sidebar, the horizontal games strip and the category page heading. Values
 * mirror the back-office pickers (ALLOWED_ICON_KEYS / ALLOWED_BADGE_KEYS /
 * ALLOWED_ACCENT_KEYS); change both sides together.
 */

// Built-in menu icons chosen in the back office (icon_key). Inline SVGs so
// they recolour with the theme.
export const MENU_ICONS = {
  casino: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M4 4h13a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Zm2 3v5h2V7H6Zm3.5 0v5h2V7h-2ZM13 7v5h2V7h-2ZM6 15v2h9v-2H6Zm14-8h1a1 1 0 0 1 1 1v6a3 3 0 0 1-3 3V7Z" /></svg>),
  "live-casino": (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4 0-8 2-8 5v1h16v-1c0-3-4-5-8-5Z" /></svg>),
  slots: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><circle cx="8" cy="16" r="3" /><circle cx="15" cy="17" r="2.5" /><path d="M9 13c1-4 4-7 8-8l1 2c-3 1-5 3-6 6l-3 0Z" /></svg>),
  "table-games": (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 3c2 3 7 6 7 10a3.5 3.5 0 0 1-6 2.6c.2 1.6.8 2.7 1.8 3.4H9.2c1-.7 1.6-1.8 1.8-3.4A3.5 3.5 0 0 1 5 13c0-4 5-7 7-10Z" /></svg>),
  jackpot: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M4 8l3 3 5-6 5 6 3-3-2 11H6L4 8Zm2 13h12v-2H6v2Z" /></svg>),
  new: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="m12 3 2.6 5.7 6.2.7-4.6 4.2 1.3 6.1L12 16.8 6.5 19.7l1.3-6.1L3.2 9.4l6.2-.7L12 3Z" /></svg>),
  roulette: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 4a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm0 4a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" /></svg>),
  blackjack: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M6 3h9a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2 3v2h2V6H8Zm5 10v2h2v-2h-2Z" /></svg>),
  poker: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 4a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm-1-4h2v3h-2V2Zm0 17h2v3h-2v-3ZM2 11h3v2H2v-2Zm17 0h3v2h-3v-2Z" /></svg>),
  crash: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M3 17l6-6 4 4 7-7v4h2V5h-7v2h3l-5 5-4-4-8 8 2 2Z" /></svg>),
  hot: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M13 2c0 3 3 4 3 8a4 4 0 0 1-8 0c0-1.5.5-2.5 1.5-3.5C10 8 11 9 11 10c1-1 2-4 2-8Z" /></svg>),
  sports: (<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 5.2 3.1 2.3-1.2 3.6h-3.8L8.9 9.5 12 7.2Z" /></svg>),
};

// Built-in menu badges chosen in the back office (badge key).
export const BADGE_STYLES = {
  new: { label: "NEW", bg: "#16a34a" },
  hot: { label: "HOT", bg: "#dc2626" },
  top: { label: "TOP", bg: "#d97706" },
  live: { label: "LIVE", bg: "#7c3aed" },
  sale: { label: "SALE", bg: "#db2777" },
  exclusive: { label: "EXCLUSIVE", bg: "#0d9488" },
};

// Built-in accent colours chosen in the back office (accent key).
export const ACCENT_COLORS = {
  gold: "#eab308",
  red: "#dc2626",
  green: "#16a34a",
  blue: "#2563eb",
  purple: "#7c3aed",
  teal: "#0d9488",
};

// A preset key maps to its themed hex; anything else (a #hex, or a CSS colour
// name) passes through verbatim, so the back office can pick ANY colour.
export const resolveAccent = (a) => (a ? (ACCENT_COLORS[a] || a) : null);

// Small coloured pill for a built-in badge key; null when there is no badge.
export const badgePill = (badge) => {
  const b = badge && BADGE_STYLES[badge];
  if (!b) {
    return null;
  }
  return (
    <span
      style={{
        background: b.bg,
        color: "#fff",
        fontSize: "9px",
        fontWeight: 800,
        letterSpacing: ".04em",
        padding: "1px 6px",
        borderRadius: "5px",
        marginLeft: "8px",
        lineHeight: 1.7,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {b.label}
    </span>
  );
};
